import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { payrollReviewGateway } from '../index';
import { reportsGateway } from '../../reports';
import type { PayrollRegisterResponse } from '../../reports/types';
import { useIdentity } from '../../identity/state/useIdentity';
import { approveBlockedReason as approveBlockedReasonFor } from '../approvalGuard';
import { isForbidden, isNotFound, payrollReviewErrorMessage } from '../payrollReviewErrors';
import { PayrollRunStatus, type PayrollReviewRun } from '../types';
import { READ_ONLY_NOTICE, REJECTED_IS_FINAL_NOTE } from '../reviewCopy';
import { PayrollRunStatusBadge } from '../components/PayrollRunStatusBadge';
import { PayrollRegisterTable } from '../components/PayrollRegisterTable';
import { ReviewSafetyNotice } from '../components/ReviewSafetyNotice';
import { TaxReadinessNotice } from '../components/TaxReadinessNotice';
import { TaxWarningsNotice, type TaxWarningsState } from '../components/TaxWarningsNotice';
import { ApproveRunDialog } from '../components/ApproveRunDialog';
import { RejectRunDialog } from '../components/RejectRunDialog';
import { formatDate, formatDateTime, formatMoney, formatPeriod } from '../formatting';
import '../payroll-review.css';

type RunState =
  | { kind: 'loading' }
  | { kind: 'ready'; run: PayrollReviewRun }
  | { kind: 'notFound' }
  | { kind: 'forbidden' }
  | { kind: 'error'; message: string };

type RegisterState =
  | { kind: 'loading' }
  | { kind: 'ready'; register: PayrollRegisterResponse }
  | { kind: 'notFound' }
  | { kind: 'forbidden' }
  | { kind: 'error'; message: string };

type Decision = 'approve' | 'reject';

/**
 * EPN-104 — one payroll run for review: its Module 7 state and the per-employee payroll register (from the
 * existing Reports endpoint), with Approve / Reject for a run awaiting approval.
 *
 * The backend is the only authority. This page never decides who may approve — it pre-disables Approve for the
 * two segregation-of-duties cases it can see from the run (the viewer created or submitted it) as a courtesy,
 * handles the server's 400 for the third (the viewer ran a calculation in it), and switches to a read-only
 * state the moment the server answers 403. Reject is also pre-disabled for the run's own creator or submitter (EPN-241: they
 * withdraw instead; the server enforces it). Approve is also withheld until the payroll register has loaded with at least one line, so a run is
 * never approved without its figures in front of the reviewer — a review-safety guard, not authorization.
 */
export function PayrollReviewDetailPage() {
  const { me } = useIdentity();
  // Switching company changes the identity without navigating or remounting the routed page, so state loaded for the
  // previous company (the run, its register, an open dialog, a sticky read-only state from a 403 — permissions differ
  // per company) would otherwise stay on screen under the new company's name. Keying by the active company remounts
  // the page: every piece of state resets and in-flight requests are aborted by their effects' cleanups.
  return <PayrollReviewDetail key={me?.activeCompanyId ?? 'no-active-company'} />;
}

function PayrollReviewDetail() {
  const { runId } = useParams<{ runId: string }>();
  const { me } = useIdentity();

  const [runState, setRunState] = useState<RunState>({ kind: 'loading' });
  const [registerState, setRegisterState] = useState<RegisterState>({ kind: 'loading' });
  const [runAttempt, setRunAttempt] = useState(0);
  const [registerAttempt, setRegisterAttempt] = useState(0);
  const [taxWarningsState, setTaxWarningsState] = useState<TaxWarningsState>({ kind: 'loading' });
  const [taxWarningsAttempt, setTaxWarningsAttempt] = useState(0);

  const [dialog, setDialog] = useState<Decision | null>(null);
  const [busy, setBusy] = useState(false);
  const [dialogError, setDialogError] = useState<string | null>(null);
  const [pageError, setPageError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  // Sticky for this page's lifetime once the server has answered 403 to approve/reject.
  const [readOnly, setReadOnly] = useState(false);
  const noticeRef = useRef<HTMLParagraphElement>(null);
  // Aborted when the run on screen changes (or the page unmounts): a decision response or confirmation re-read that
  // lands after the user has moved to another run is ignored, so it can never overwrite the run now being shown.
  const runScopeRef = useRef<AbortController | null>(null);

  useEffect(() => {
    if (!runId) return;
    const controller = new AbortController();
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setRunState({ kind: 'loading' });
    payrollReviewGateway
      .getRun(runId, controller.signal)
      .then((run) => {
        if (!controller.signal.aborted) setRunState({ kind: 'ready', run });
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        if (isNotFound(err)) setRunState({ kind: 'notFound' });
        else if (isForbidden(err)) setRunState({ kind: 'forbidden' });
        else setRunState({ kind: 'error', message: payrollReviewErrorMessage(err, 'Could not load this payroll run.') });
      });
    return () => controller.abort();
  }, [runId, runAttempt]);

  useEffect(() => {
    if (!runId) return;
    const controller = new AbortController();
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setRegisterState({ kind: 'loading' });
    reportsGateway
      .getPayrollRegister(runId, controller.signal)
      .then((register) => {
        if (!controller.signal.aborted) setRegisterState({ kind: 'ready', register });
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        if (isForbidden(err)) setRegisterState({ kind: 'forbidden' });
        else if (isNotFound(err)) setRegisterState({ kind: 'notFound' });
        else setRegisterState({ kind: 'error', message: payrollReviewErrorMessage(err, 'Could not load the payroll register.') });
      });
    return () => controller.abort();
  }, [runId, registerAttempt]);

  // EPN-22: the attached calculations, read only for their tax warnings. Informational: a failure here never changes what Approve allows.
  useEffect(() => {
    if (!runId) return;
    const controller = new AbortController();
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setTaxWarningsState({ kind: 'loading' });
    payrollReviewGateway
      .listRunCalculations(runId, controller.signal)
      .then((calculations) => {
        if (!controller.signal.aborted) setTaxWarningsState({ kind: 'ready', calculations: Array.isArray(calculations) ? calculations : [] });
      })
      .catch(() => {
        if (!controller.signal.aborted) setTaxWarningsState({ kind: 'error' });
      });
    return () => controller.abort();
  }, [runId, taxWarningsAttempt]);

  useEffect(() => {
    if (notice) noticeRef.current?.focus();
  }, [notice]);

  useEffect(() => {
    const scope = new AbortController();
    runScopeRef.current = scope;
    return () => scope.abort();
  }, [runId]);

  const decide = useCallback(
    async (decision: Decision, reason?: string) => {
      const scope = runScopeRef.current;
      if (!runId || !scope) return;
      const isCurrent = () => !scope.signal.aborted;
      setBusy(true);
      setDialogError(null);
      setPageError(null);
      try {
        const updated = decision === 'approve' ? await payrollReviewGateway.approveRun(runId) : await payrollReviewGateway.rejectRun(runId, reason ?? '');
        // The user moved to another run while this was in flight: the decision is recorded server-side; what is on
        // screen now belongs to the other run and must not be touched.
        if (!isCurrent()) return;
        setRunState({ kind: 'ready', run: updated });
        setDialog(null);
        setNotice(decision === 'approve' ? 'Payroll run approved.' : 'Payroll run rejected.');
        // Confirm against the server's record (the response above is already the server's answer; this just
        // guarantees what is displayed is what a reload would show). A failure here changes nothing, and a result
        // that arrives after the user has moved to another run is dropped.
        payrollReviewGateway
          .getRun(runId, scope.signal)
          .then((fresh) => {
            if (isCurrent()) setRunState({ kind: 'ready', run: fresh });
          })
          .catch(() => undefined);
      } catch (err) {
        if (!isCurrent()) return;
        if (isForbidden(err)) {
          setReadOnly(true);
          setDialog(null);
          return;
        }
        const message = payrollReviewErrorMessage(err, decision === 'approve' ? 'Could not approve this payroll run.' : 'Could not reject this payroll run.');
        if (isNotFound(err)) {
          setDialog(null);
          setRunState({ kind: 'notFound' });
          return;
        }
        // A 400/409 may mean someone else already acted: re-read the run, and keep the dialog open only if the
        // run is still awaiting approval (or we cannot tell), so a typed rejection reason is never lost.
        let fresh: PayrollReviewRun | null = null;
        try {
          fresh = await payrollReviewGateway.getRun(runId, scope.signal);
          if (!isCurrent()) return;
          setRunState({ kind: 'ready', run: fresh });
        } catch {
          fresh = null;
        }
        if (!isCurrent()) return;
        if (fresh && fresh.status !== PayrollRunStatus.PendingApproval) {
          setDialog(null);
          setPageError(message);
        } else {
          setDialogError(message);
        }
      } finally {
        setBusy(false);
      }
    },
    [runId],
  );

  const backLink = (
    <Link to="/payroll-review" className="payroll-review__back">
      ← Back to Payroll Review
    </Link>
  );

  if (runState.kind === 'loading') {
    return (
      <section className="card card--wide payroll-review">
        <div className="section-header"><h2>Payroll Review</h2></div>
        <p className="muted" role="status">Loading payroll run…</p>
      </section>
    );
  }

  if (runState.kind !== 'ready') {
    return (
      <section className="card card--wide payroll-review">
        <div className="section-header"><h2>Payroll Review</h2></div>
        {runState.kind === 'notFound' && (
          <div className="payroll-review__panel" role="alert">
            <strong>Payroll run not found.</strong>
            <p className="muted">It may have been removed, or it may belong to a different company.</p>
          </div>
        )}
        {runState.kind === 'forbidden' && (
          <div className="payroll-review__panel" role="alert">
            <strong>You don&apos;t have permission to view this payroll run.</strong>
          </div>
        )}
        {runState.kind === 'error' && (
          <div className="payroll-review__panel payroll-review__panel--error" role="alert">
            <p className="error">{runState.message}</p>
            <button type="button" onClick={() => setRunAttempt((n) => n + 1)}>Retry</button>
          </div>
        )}
        <p>{backLink}</p>
      </section>
    );
  }

  const run = runState.run;
  const period = formatPeriod(run.periodStartDate, run.periodEndDate);
  const isPending = run.status === PayrollRunStatus.PendingApproval;
  const registerLines = registerState.kind === 'ready' ? registerState.register.lines.length : 0;
  const employeeNames: ReadonlyMap<string, string> = new Map(
    registerState.kind === 'ready' ? registerState.register.lines.map((l) => [l.employeeId, l.employeeName]) : [],
  );

  // Why Approve is unavailable, if it is — shared with Custom Pay Run's Pending Payroll step (approvalGuard.ts).
  const approveBlockedReason = approveBlockedReasonFor({ viewerUserId: me?.userId, run, registerKind: registerState.kind, registerLines });
  const canApprove = isPending && !readOnly && !busy && approveBlockedReason === null;
  // EPN-241: the run's own creator or submitter cannot reject it (the server refuses); they withdraw it instead.
  const ownRun = !!me?.userId && (me.userId === run.createdByUserId || me.userId === run.submittedByUserId);
  const netTotal = registerState.kind === 'ready' ? formatMoney(registerState.register.totals.netPay) : '';

  return (
    <section className="card card--wide payroll-review">
      <div className="section-header">
        <h2>Payroll Review</h2>
        {backLink}
      </div>

      <h3>Payroll run: {period}</h3>

      {notice && (
        <p ref={noticeRef} tabIndex={-1} className="success" role="status">
          {notice}
        </p>
      )}
      {pageError && (
        <p className="error" role="alert">
          {pageError}
        </p>
      )}

      <ReviewSafetyNotice />

      <dl className="payroll-review__meta">
        <dt>Status</dt>
        <dd><PayrollRunStatusBadge status={run.status} /></dd>
        <dt>Pay period</dt>
        <dd>{period}</dd>
        <dt>Check date</dt>
        <dd>{formatDate(run.checkDate)}</dd>
        <dt>Created by</dt>
        <dd>{run.createdByEmailSnapshot} · {formatDateTime(run.createdAtUtc)}</dd>
        <dt>Submitted by</dt>
        <dd>{run.submittedByEmailSnapshot ? `${run.submittedByEmailSnapshot} · ${formatDateTime(run.submittedForApprovalAtUtc)}` : '—'}</dd>
        {run.status === PayrollRunStatus.Approved && (
          <>
            <dt>Approved by</dt>
            <dd>{run.approvedByEmailSnapshot ?? '—'} · {formatDateTime(run.approvedAtUtc)}</dd>
          </>
        )}
        {run.status === PayrollRunStatus.Rejected && (
          <>
            <dt>Rejected by</dt>
            <dd>{run.rejectedByEmailSnapshot ?? '—'} · {formatDateTime(run.rejectedAtUtc)}</dd>
            <dt>Rejection reason</dt>
            <dd className="payroll-review__reason">{run.rejectionReason ?? '—'}</dd>
          </>
        )}
        {run.status === PayrollRunStatus.Withdrawn && (
          <>
            <dt>Withdrawn by</dt>
            <dd>{run.rejectedByEmailSnapshot ?? '—'} · {formatDateTime(run.rejectedAtUtc)}</dd>
            <dt>Reason</dt>
            <dd className="payroll-review__reason">{run.rejectionReason ?? '—'}</dd>
          </>
        )}
      </dl>

      {run.status === PayrollRunStatus.Rejected && <p className="muted">{REJECTED_IS_FINAL_NOTE}</p>}
      {run.status === PayrollRunStatus.Withdrawn && (
        <p className="muted">The person who prepared this run withdrew it before it was approved. It is final: start a new Custom Pay Run to try again.</p>
      )}

      {isPending && (
        <TaxReadinessNotice
          readiness={run.taxReadiness}
          employeeNames={employeeNames}
        />
      )}

      <TaxWarningsNotice state={taxWarningsState} employeeNames={employeeNames} onRetry={() => setTaxWarningsAttempt((n) => n + 1)} />

      <h3>Payroll register</h3>
      {registerState.kind === 'loading' && <p className="muted" role="status">Loading payroll register…</p>}
      {registerState.kind === 'forbidden' && (
        <div className="payroll-review__panel" role="alert">
          <strong>You don&apos;t have permission to view the payroll register for this run.</strong>
        </div>
      )}
      {(registerState.kind === 'error' || registerState.kind === 'notFound') && (
        <div className="payroll-review__panel payroll-review__panel--error" role="alert">
          <p className="error">
            {registerState.kind === 'error' ? registerState.message : 'The payroll register for this run could not be found.'}
          </p>
          <button type="button" onClick={() => setRegisterAttempt((n) => n + 1)}>Retry</button>
        </div>
      )}
      {registerState.kind === 'ready' && registerLines === 0 && (
        <div className="payroll-review__panel" role="alert">
          <strong>No payroll figures are available to review for this run.</strong>
          <p className="muted">The payroll register returned no employee lines, so this run cannot be approved from here.</p>
        </div>
      )}
      {registerState.kind === 'ready' && registerLines > 0 && <PayrollRegisterTable register={registerState.register} />}

      {readOnly && (
        <p className="muted" role="status">
          {READ_ONLY_NOTICE}
        </p>
      )}

      {isPending && !readOnly && (
        <>
          <div className="payroll-review__actions">
            <button
              type="button"
              className="payroll-review__primary"
              disabled={!canApprove}
              aria-describedby={approveBlockedReason ? 'payroll-review-approve-help' : undefined}
              onClick={() => { setDialogError(null); setDialog('approve'); }}
            >
              Approve
            </button>
            <button
              type="button"
              className="payroll-review__danger"
              disabled={busy || ownRun}
              aria-describedby={ownRun ? 'payroll-review-reject-help' : undefined}
              onClick={() => { setDialogError(null); setDialog('reject'); }}
            >
              Reject
            </button>
          </div>
          {ownRun && (
            <p id="payroll-review-reject-help" className="muted">
              You created or submitted this payroll run, so you can&apos;t reject it. To take it back, withdraw it from Custom Pay Run.
            </p>
          )}
          {approveBlockedReason && (
            <p id="payroll-review-approve-help" className="muted">
              {approveBlockedReason}
            </p>
          )}
        </>
      )}

      {dialog === 'approve' && (
        <ApproveRunDialog
          periodLabel={period}
          checkDateLabel={formatDate(run.checkDate)}
          netPayLabel={netTotal}
          busy={busy}
          error={dialogError}
          onConfirm={() => void decide('approve')}
          onCancel={() => setDialog(null)}
        />
      )}
      {dialog === 'reject' && (
        <RejectRunDialog
          periodLabel={period}
          busy={busy}
          error={dialogError}
          onConfirm={(reason) => void decide('reject', reason)}
          onCancel={() => setDialog(null)}
        />
      )}
    </section>
  );
}
